// Claude Code's headless output (`claude -p --output-format stream-json`)
// turned into what the dashboard chat shows (docs/features/26-live-chat.md).
//
// Every line the CLI prints lands somewhere: a known event updates the
// timeline, and anything this file does not know yet becomes an `unknown`
// block holding the raw line, so a new Claude Code version can never make
// output silently disappear. `KNOWN` lists what is handled on purpose; the
// contract test fails when a recorded session holds anything else, which is
// how a Claude Code upgrade is checked before it ships (scripts/contract.mjs).

// The CLI's lines are loosely typed on purpose: fields come and go between
// versions, and every reader below tolerates a missing one.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Raw = Record<string, any>

export type ToolStatus = 'streaming' | 'running' | 'done' | 'error' | 'denied' | 'interrupted'

export type Block =
  | {
      kind: 'user'; id: string; text: string; images: number; at: number | null; queued?: boolean
      /** The last top-level assistant line before it: where a rewind to this message resumes (null: nothing before it). */
      prev?: string | null
    }
  | { kind: 'text'; id: string; text: string; streaming: boolean }
  | { kind: 'thinking'; id: string; text: string; streaming: boolean; redacted: boolean; ms: number | null }
  | {
      kind: 'tool'; id: string; name: string; input: Raw; inputText: string; status: ToolStatus
      /** The tool's answer as Claude saw it (text, or content blocks), and the CLI's structured result (diff, exit code…). */
      result: { content: unknown; text: string; images: number; isError: boolean; structured: unknown } | null
      /** A subagent's or background task's work, nested under the call that started it. */
      children: Block[]
      task: Task | null
      server: boolean
    }
  | {
      kind: 'permission'; id: string; tool: string; input: Raw; description: string | null
      suggestions: unknown[]; toolUseId: string | null; answer: 'allow' | 'deny' | 'cancelled' | null
    }
  | { kind: 'compact'; id: string; trigger: string | null; preTokens: number | null }
  | { kind: 'notice'; id: string; level: 'info' | 'warn' | 'error'; title: string; text: string; raw: Raw }
  | {
      kind: 'result'; id: string; ok: boolean; subtype: string; text: string; costUsd: number | null; ms: number | null
      turns: number | null; usage: Raw | null; errors: string[]; denials: number
    }
  | { kind: 'unknown'; id: string; label: string; raw: Raw }

export type Task = {
  id: string; toolUseId: string | null; description: string; type: string | null; status: string
  lastTool: string | null; summary: string | null; outputFile: string | null; background: boolean
}

export type Session = {
  id: string | null; model: string | null; version: string | null; cwd: string | null
  permissionMode: string | null; apiKeySource: string | null; tools: string[]
  mcp: Array<{ name: string; status: string }>; slashCommands: string[]
}

/** What Claude is doing right now: the footer's live line. */
export type Activity = 'idle' | 'requesting' | 'thinking' | 'writing' | 'tool' | 'compacting' | 'waiting' | 'exited'

export type RateLimit = { status: string; type: string | null; resetsAt: number | null; utilization: number | null }

/** Every `type` or `type/subtype` this file handles on purpose. */
export const KNOWN = [
  'system/init', 'system/status', 'system/thinking_tokens', 'system/compact_boundary', 'system/api_retry',
  'system/permission_denied', 'system/hook_started', 'system/hook_progress', 'system/hook_response',
  'system/task_started', 'system/task_progress', 'system/task_updated', 'system/task_notification',
  'system/background_tasks_changed', 'system/local_command_output', 'system/files_persisted',
  'stream_event', 'assistant', 'user', 'result', 'rate_limit_event', 'tool_progress', 'auth_status', 'tool_use_summary',
  'control_request', 'control_response', 'control_cancel_request', 'keep_alive',
  // Lines the wrapper (images/base/bin/trisof-claude) adds around Claude's own.
  'trisof',
] as const

/** Assistant content blocks drawn as such; anything else is shown raw. */
export const KNOWN_BLOCKS = ['text', 'thinking', 'redacted_thinking', 'tool_use', 'server_tool_use', 'web_search_tool_result', 'web_fetch_tool_result', 'mcp_tool_use', 'mcp_tool_result'] as const

/** The label a line is known by: `type`, or `type/subtype` for system lines. */
export function labelOf(o: Raw): string {
  return o.type === 'system' ? `system/${o.subtype ?? ''}` : String(o.type ?? '?')
}

export const isKnown = (o: Raw) => (KNOWN as readonly string[]).includes(labelOf(o))

type Draft = { key: string; msg: string; index: number; block: Block; parent: string | null }

/**
 * The chat as it stands after a run of lines. Mutable and fed line by line
 * (`push`), because a session sends thousands of small deltas; `version`
 * changes whenever something visible did, for the UI to redraw on.
 */
export class Timeline {
  blocks: Block[] = []
  session: Session = { id: null, model: null, version: null, cwd: null, permissionMode: null, apiKeySource: null, tools: [], mcp: [], slashCommands: [] }
  activity: Activity = 'idle'
  /** Stops sent to Claude (the wrapper's `sent` notes), answered by a control_response. */
  private interrupts = new Set<string>()
  thinkingTokens = 0
  rateLimit: RateLimit | null = null
  /** The last turn's context size (input + cache tokens), for the context meter. */
  contextTokens = 0
  costUsd = 0
  tasks = new Map<string, Task>()
  hooks: Array<{ id: string; name: string; event: string; outcome: string | null; output: string }> = []
  version = 0

  private tools = new Map<string, Extract<Block, { kind: 'tool' }>>()
  private perms = new Map<string, Extract<Block, { kind: 'permission' }>>()
  private drafts = new Map<string, Draft>()
  private seenUsers = new Set<string>()
  /** The newest top-level assistant line's uuid, for the next user block's `prev`. */
  private lastAssistant: string | null = null
  private n = 0

  push(o: Raw): void {
    this.version++
    try {
      this.apply(o)
    } catch (e) {
      // A line shaped unlike any version we know: show it rather than lose it.
      this.add({ kind: 'unknown', id: this.id('bad'), label: `${labelOf(o)} (unreadable: ${(e as Error).message})`, raw: o }, null)
    }
  }

  pushAll(lines: Iterable<Raw>): this {
    for (const l of lines) this.push(l)
    return this
  }

  private id(p: string) { return `${p}-${++this.n}` }

  /** Where a block goes: under its subagent's call when it has one, else the top. */
  private add(b: Block, parent: string | null | undefined): void {
    const host = parent ? this.tools.get(parent) : undefined
    ;(host ? host.children : this.blocks).push(b)
  }

  private apply(o: Raw): void {
    switch (o.type) {
      case 'system': return this.system(o)
      case 'stream_event': return this.stream(o)
      case 'assistant': return this.assistant(o)
      case 'user': return this.user(o)
      case 'result': return this.result(o)
      case 'rate_limit_event': return this.rate(o)
      case 'control_request': return this.controlRequest(o)
      case 'control_response': return this.controlResponse(o)
      case 'control_cancel_request': {
        const p = this.perms.get(o.request_id)
        if (p && !p.answer) p.answer = 'cancelled'
        return
      }
      case 'keep_alive': return
      case 'tool_progress': {
        const t = this.tools.get(o.tool_use_id)
        if (t && t.status === 'streaming') t.status = 'running'
        return
      }
      case 'auth_status':
        if (o.error) this.notice('error', 'Sign-in', String(o.error), o)
        return
      case 'tool_use_summary':
        return this.notice('info', 'Summary', String(o.summary ?? ''), o)
      case 'trisof': return this.wrapper(o)
      default:
        this.add({ kind: 'unknown', id: this.id('u'), label: labelOf(o), raw: o }, o.parent_tool_use_id)
    }
  }

  private notice(level: 'info' | 'warn' | 'error', title: string, text: string, raw: Raw, parent?: string | null) {
    this.add({ kind: 'notice', id: this.id('n'), level, title, text, raw }, parent ?? null)
  }

  private system(o: Raw): void {
    switch (o.subtype) {
      case 'init': {
        const first = !this.session.id
        this.session = {
          id: o.session_id ?? null, model: o.model ?? null, version: o.claude_code_version ?? null, cwd: o.cwd ?? null,
          permissionMode: o.permissionMode ?? null, apiKeySource: o.apiKeySource ?? null,
          tools: Array.isArray(o.tools) ? o.tools : [],
          mcp: Array.isArray(o.mcp_servers) ? o.mcp_servers.map((m: Raw) => ({ name: String(m.name), status: String(m.status) })) : [],
          slashCommands: Array.isArray(o.slash_commands) ? o.slash_commands : [],
        }
        // The CLI says init again at every turn; only a change is news.
        const failed = this.session.mcp.filter((m) => m.status === 'failed')
        if (first && failed.length) this.notice('warn', 'MCP', `Not connected: ${failed.map((m) => m.name).join(', ')}`, o)
        return
      }
      case 'status':
        if (o.permissionMode) this.session.permissionMode = o.permissionMode
        if (o.status === 'compacting') this.activity = 'compacting'
        else if (o.status === 'requesting') this.activity = 'requesting'
        else if (this.activity === 'compacting' || this.activity === 'requesting') this.activity = 'idle'
        if (o.compact_result && o.compact_result !== 'success') this.notice('warn', 'Compact', `Compaction ${o.compact_result}`, o)
        return
      case 'thinking_tokens':
        this.thinkingTokens = Number(o.estimated_tokens ?? 0)
        this.activity = 'thinking'
        return
      case 'compact_boundary':
        this.add({ kind: 'compact', id: this.id('c'), trigger: o.compact_metadata?.trigger ?? null, preTokens: o.compact_metadata?.pre_tokens ?? null }, null)
        return
      case 'api_retry':
        this.notice(o.error_status === 401 || o.error === 'authentication_failed' ? 'error' : 'warn', 'Retrying',
          `Attempt ${o.attempt}/${o.max_retries}: ${o.error ?? 'error'}${o.error_status ? ` (${o.error_status})` : ''}, next in ${Math.round(Number(o.retry_delay_ms ?? 0) / 100) / 10}s`, o)
        return
      case 'permission_denied': {
        const t = this.tools.get(o.tool_use_id)
        if (t) t.status = 'denied'
        this.notice('warn', 'Not allowed', String(o.message ?? `${o.tool_name} was refused`), o, t ? null : o.parent_tool_use_id)
        return
      }
      case 'hook_started':
        this.hooks.push({ id: String(o.hook_id ?? this.id('h')), name: String(o.hook_name ?? ''), event: String(o.hook_event ?? ''), outcome: null, output: '' })
        return
      case 'hook_progress':
      case 'hook_response': {
        const h = this.hooks.find((x) => x.id === o.hook_id)
        const out = [o.output, o.stdout, o.stderr].filter(Boolean).join('\n')
        if (h) { h.outcome = o.outcome ?? h.outcome; h.output = out || h.output }
        // A failing hook is something to see; a passing one is noise.
        if (o.subtype === 'hook_response' && (o.outcome === 'error' || (o.exit_code ?? 0) !== 0)) {
          this.notice('warn', `Hook ${o.hook_name ?? ''}`.trim(), out || `exit ${o.exit_code}`, o)
        }
        return
      }
      case 'task_started':
      case 'task_progress':
      case 'task_updated':
      case 'task_notification':
        return this.task(o)
      case 'background_tasks_changed':
        for (const t of Array.isArray(o.tasks) ? o.tasks : []) {
          const k = String(t.task_id ?? t.id ?? '')
          if (!k) continue
          const cur = this.tasks.get(k)
          if (cur) cur.status = String(t.status ?? cur.status)
        }
        return
      case 'local_command_output':
        return this.notice('info', 'Command', String(o.content ?? o.output ?? ''), o)
      case 'files_persisted':
        return
      default:
        this.add({ kind: 'unknown', id: this.id('u'), label: labelOf(o), raw: o }, o.parent_tool_use_id)
    }
  }

  private task(o: Raw): void {
    const id = String(o.task_id)
    let t = this.tasks.get(id)
    if (!t) {
      t = { id, toolUseId: o.tool_use_id ?? null, description: String(o.description ?? ''), type: o.task_type ?? null, status: 'running', lastTool: null, summary: null, outputFile: null, background: !!o.is_backgrounded }
      this.tasks.set(id, t)
    }
    if (o.tool_use_id) t.toolUseId = o.tool_use_id
    if (o.description) t.description = String(o.description)
    if (o.last_tool_name) t.lastTool = String(o.last_tool_name)
    if (o.subtype === 'task_updated' && o.patch?.status) t.status = String(o.patch.status)
    if (o.subtype === 'task_notification') {
      t.status = String(o.status ?? 'completed')
      t.summary = o.summary ?? null
      t.outputFile = o.output_file ?? null
    }
    const host = t.toolUseId ? this.tools.get(t.toolUseId) : undefined
    if (host) host.task = t
  }

  // ---- streaming drafts ----------------------------------------------------

  private stream(o: Raw): void {
    const e = o.event ?? {}
    const msg = String(o.api_message_id ?? e.message?.id ?? 'm')
    const parent: string | null = o.parent_tool_use_id ?? null
    const key = `${msg}:${e.index}`
    switch (e.type) {
      case 'message_start':
        this.activity = 'requesting'
        return
      case 'content_block_start': {
        const cb = e.content_block ?? {}
        let b: Block
        if (cb.type === 'text') { b = { kind: 'text', id: key, text: cb.text ?? '', streaming: true }; this.activity = 'writing' }
        else if (cb.type === 'thinking' || cb.type === 'redacted_thinking') { b = { kind: 'thinking', id: key, text: cb.thinking ?? '', streaming: true, redacted: cb.type === 'redacted_thinking', ms: null }; this.activity = 'thinking' }
        else if (cb.type === 'tool_use' || cb.type === 'server_tool_use' || cb.type === 'mcp_tool_use') {
          b = this.newTool(cb.id ?? key, cb.name ?? '?', cb.input ?? {}, cb.type !== 'tool_use')
          b.status = 'streaming'
          this.activity = 'tool'
        } else return // a result block (web search…) arrives whole with its message
        this.drafts.set(key, { key, msg, index: Number(e.index), block: b, parent })
        this.add(b, parent)
        return
      }
      case 'content_block_delta': {
        const d = this.drafts.get(key)
        const delta = e.delta ?? {}
        if (!d) return
        if (delta.type === 'text_delta' && d.block.kind === 'text') d.block.text += delta.text ?? ''
        else if (delta.type === 'thinking_delta' && d.block.kind === 'thinking') d.block.text += delta.thinking ?? ''
        else if (delta.type === 'input_json_delta' && d.block.kind === 'tool') d.block.inputText += delta.partial_json ?? ''
        return
      }
      case 'content_block_stop': {
        const d = this.drafts.get(key)
        if (d && d.block.kind === 'tool' && d.block.status === 'streaming') {
          try { d.block.input = JSON.parse(d.block.inputText || '{}') } catch { /* the final message has it */ }
          d.block.status = 'running'
        }
        return
      }
      case 'message_delta':
      case 'message_stop':
      case 'ping':
        return
      default:
        // An event type the API added: keep it visible.
        this.add({ kind: 'unknown', id: this.id('u'), label: `stream_event/${e.type}`, raw: o }, parent)
    }
  }

  /** The draft an assistant line's block finalizes: the lowest open one of its message, of the same kind. */
  private takeDraft(msg: string, kind: Block['kind'], toolId?: string): Draft | undefined {
    let best: Draft | undefined
    for (const d of this.drafts.values()) {
      if (d.msg !== msg || d.block.kind !== kind) continue
      if (toolId && d.block.id !== toolId) continue
      if (!best || d.index < best.index) best = d
    }
    if (best) this.drafts.delete(best.key)
    return best
  }

  private newTool(id: string, name: string, input: Raw, server: boolean): Extract<Block, { kind: 'tool' }> {
    const t: Extract<Block, { kind: 'tool' }> = { kind: 'tool', id, name, input, inputText: '', status: 'running', result: null, children: [], task: null, server }
    this.tools.set(id, t)
    for (const task of this.tasks.values()) if (task.toolUseId === id) t.task = task
    return t
  }

  // ---- whole messages ------------------------------------------------------

  private assistant(o: Raw): void {
    const m = o.message ?? {}
    const msg = String(m.id ?? o.uuid ?? 'm')
    const parent: string | null = o.parent_tool_use_id ?? null
    if (o.error || o.is_api_error_message || m.model === '<synthetic>') {
      const text = (Array.isArray(m.content) ? m.content : []).map((b: Raw) => b.text ?? '').join('\n')
      // `/cost` and other local commands answer as a synthetic message too.
      if (!o.error && !o.is_api_error_message) return this.notice('info', 'Claude Code', text, o, parent)
      return this.notice('error', o.error ? `Error: ${o.error}` : 'API error', text || String(o.error ?? ''), o, parent)
    }
    for (const b of Array.isArray(m.content) ? m.content : []) {
      switch (b.type) {
        case 'text': {
          const d = this.takeDraft(msg, 'text')
          if (d && d.block.kind === 'text') { d.block.text = b.text ?? d.block.text; d.block.streaming = false }
          else this.add({ kind: 'text', id: this.id('t'), text: b.text ?? '', streaming: false }, parent)
          break
        }
        case 'thinking':
        case 'redacted_thinking': {
          const d = this.takeDraft(msg, 'thinking')
          const text = b.type === 'thinking' ? (b.thinking ?? '') : ''
          if (d && d.block.kind === 'thinking') Object.assign(d.block, { text: text || d.block.text, streaming: false, redacted: b.type === 'redacted_thinking', ms: o.thinking_duration_ms ?? null })
          else this.add({ kind: 'thinking', id: this.id('th'), text, streaming: false, redacted: b.type === 'redacted_thinking', ms: o.thinking_duration_ms ?? null }, parent)
          break
        }
        case 'tool_use':
        case 'server_tool_use':
        case 'mcp_tool_use': {
          const d = this.takeDraft(msg, 'tool', b.id)
          if (d && d.block.kind === 'tool') { d.block.input = b.input ?? d.block.input; if (d.block.status === 'streaming') d.block.status = 'running' }
          else if (!this.tools.has(b.id)) this.add(this.newTool(b.id, b.name ?? '?', b.input ?? {}, b.type !== 'tool_use'), parent)
          this.activity = 'tool'
          break
        }
        case 'web_search_tool_result':
        case 'web_fetch_tool_result':
        case 'mcp_tool_result': {
          const t = this.tools.get(b.tool_use_id)
          if (t) this.finish(t, b.content, !!b.is_error, null)
          else this.add({ kind: 'unknown', id: this.id('u'), label: `assistant/${b.type}`, raw: b }, parent)
          break
        }
        default:
          this.add({ kind: 'unknown', id: this.id('u'), label: `assistant/${b.type}`, raw: b }, parent)
      }
    }
    if (!parent && o.uuid) this.lastAssistant = String(o.uuid)
    const u = m.usage
    if (u && !parent) this.contextTokens = Number(u.input_tokens ?? 0) + Number(u.cache_read_input_tokens ?? 0) + Number(u.cache_creation_input_tokens ?? 0)
  }

  private finish(t: Extract<Block, { kind: 'tool' }>, content: unknown, isError: boolean, structured: unknown): void {
    const parts = Array.isArray(content) ? content : [content]
    const text = parts.map((p) => typeof p === 'string' ? p : (p as Raw)?.type === 'text' ? (p as Raw).text : '').filter(Boolean).join('\n')
    const images = parts.filter((p) => (p as Raw)?.type === 'image').length
    t.result = { content, text, images, isError, structured }
    if (t.status === 'denied') return
    // "User rejected tool use" is the CLI's answer both to a refusal and to an
    // interrupt; a refusal has its permission request, an interrupt has none.
    if (isError && structured === 'User rejected tool use') {
      const refused = [...this.perms.values()].some((p) => p.answer === 'deny' && (p.toolUseId === t.id || p.tool === t.name))
      t.status = refused ? 'denied' : 'interrupted'
      return
    }
    t.status = isError ? 'error' : 'done'
  }

  private user(o: Raw): void {
    const m = o.message ?? {}
    const parent: string | null = o.parent_tool_use_id ?? null
    const content: Raw[] = typeof m.content === 'string' ? [{ type: 'text', text: m.content }] : Array.isArray(m.content) ? m.content : []
    const results = content.filter((b) => b.type === 'tool_result')
    for (const b of results) {
      const t = this.tools.get(b.tool_use_id)
      if (t) this.finish(t, b.content, !!b.is_error, o.tool_use_result ?? null)
      else this.add({ kind: 'unknown', id: this.id('u'), label: 'user/tool_result (no call)', raw: b }, parent)
    }
    const texts = content.filter((b) => b.type === 'text').map((b) => String(b.text ?? ''))
    const images = content.filter((b) => b.type === 'image').length
    if (!texts.length && !images) return
    const text = texts.join('\n')
    if (/^\[Request interrupted by user/.test(text)) {
      for (const t of this.tools.values()) if (t.status === 'running' || t.status === 'streaming') t.status = 'interrupted'
      return this.notice('warn', 'Interrupted', text.replace(/^\[|\]$/g, ''), o, parent)
    }
    // Subagent prompts and the CLI's own notes (task results, reminders) are not the person typing.
    if (parent || o.isSynthetic) return this.notice('info', parent ? 'Prompt' : 'Note', text, o, parent)
    // The echo of what we sent (--replay-user-messages) is the one copy we show.
    const key = o.uuid ?? text
    if (this.seenUsers.has(key)) return
    this.seenUsers.add(key)
    this.add({ kind: 'user', id: o.uuid ?? this.id('me'), text, images, at: o.timestamp ? Date.parse(o.timestamp) : null, queued: o.priority === 'next' || o.priority === 'later', prev: o.uuid ? this.lastAssistant : undefined }, null)
    this.activity = 'requesting'
  }

  private result(o: Raw): void {
    const ok = o.subtype === 'success' && !o.is_error
    for (const t of this.tools.values()) if (t.status === 'running' || t.status === 'streaming') t.status = ok ? 'done' : 'interrupted'
    for (const d of this.drafts.values()) if ('streaming' in d.block) d.block.streaming = false
    this.drafts.clear()
    if (typeof o.total_cost_usd === 'number') this.costUsd = o.total_cost_usd
    this.add({
      kind: 'result', id: o.uuid ?? this.id('r'), ok, subtype: String(o.subtype ?? ''), text: typeof o.result === 'string' ? o.result : '',
      costUsd: o.total_cost_usd ?? null, ms: o.duration_ms ?? null, turns: o.num_turns ?? null, usage: o.usage ?? null,
      errors: Array.isArray(o.errors) ? o.errors.map(String) : [], denials: Array.isArray(o.permission_denials) ? o.permission_denials.length : 0,
    }, null)
    this.activity = 'idle'
  }

  private rate(o: Raw): void {
    const r = o.rate_limit_info ?? {}
    const w = r.rateLimitType && r.unifiedWindows?.[r.rateLimitType]
    this.rateLimit = { status: String(r.status ?? 'allowed'), type: r.rateLimitType ?? null, resetsAt: r.resetsAt ?? null, utilization: w?.utilization ?? null }
    if (r.status && r.status !== 'allowed') {
      const when = r.resetsAt ? new Date(Number(r.resetsAt) * 1000).toISOString() : 'later'
      this.notice(r.status === 'rejected' ? 'error' : 'warn', 'Usage limit', `${r.rateLimitType ?? 'limit'} ${r.status === 'rejected' ? 'reached' : 'close'}; resets ${when}`, o)
    }
  }

  private controlRequest(o: Raw): void {
    const r = o.request ?? {}
    if (r.subtype === 'can_use_tool') {
      const p: Extract<Block, { kind: 'permission' }> = {
        kind: 'permission', id: String(o.request_id), tool: String(r.tool_name ?? '?'), input: r.input ?? {},
        description: r.description ?? r.decision_reason ?? null, suggestions: r.permission_suggestions ?? [], toolUseId: r.tool_use_id ?? null, answer: null,
      }
      this.perms.set(p.id, p)
      this.add(p, r.agent_id ? null : null)
      this.activity = 'waiting'
      return
    }
    // The CLI asking the host something else (hook callbacks, MCP messages): shown, never dropped.
    this.notice('info', `Request: ${r.subtype ?? '?'}`, '', o)
  }

  private controlResponse(o: Raw): void {
    const r = o.response ?? {}
    const p = this.perms.get(String(r.request_id))
    if (p) {
      p.answer = r.response?.behavior === 'allow' ? 'allow' : 'deny'
      if (this.activity === 'waiting') this.activity = 'tool'
      return
    }
    // Claude accepted a Stop: the turn is over now, whether or not a result follows.
    if (this.interrupts.delete(String(r.request_id)) && r.subtype !== 'error') {
      for (const t of this.tools.values()) if (t.status === 'running' || t.status === 'streaming') t.status = 'interrupted'
      for (const d of this.drafts.values()) if ('streaming' in d.block) d.block.streaming = false
      this.drafts.clear()
      this.activity = 'idle'
      return this.notice('info', 'Stopped', 'Stopped.', o)
    }
    if (r.subtype === 'error') this.notice('warn', 'Control', String(r.error ?? 'refused'), o)
  }

  private wrapper(o: Raw): void {
    switch (o.subtype) {
      case 'started': return this.notice('info', 'Started', `Claude ${o.resume ? 'resumed' : 'started'}`, o)
      case 'exited':
        this.activity = 'exited'
        return this.notice(o.code === 0 ? 'info' : 'error', 'Claude stopped', o.code === 0 ? 'Claude exited.' : `Claude exited with ${o.code}${o.stderr ? `: ${o.stderr}` : ''}`, o)
      // What the wrapper forwarded to Claude; the message itself comes back as its replay.
      case 'sent':
        if (o.kind === 'control_request/interrupt' && o.request_id) this.interrupts.add(String(o.request_id))
        return
      // Rewound: Claude resumed cut before this message; it and what followed are gone.
      case 'rewound': {
        const at = this.blocks.findIndex((b) => b.kind === 'user' && b.id === o.drop)
        if (at < 0) return
        const gone = this.blocks.splice(at)
        const prev = gone[0]?.kind === 'user' ? gone[0].prev : null
        this.lastAssistant = prev ?? null
        for (const b of gone) if (b.kind === 'user') this.seenUsers.delete(b.id)
        this.activity = 'idle'
        return this.notice('info', 'Rewound', 'Rewound to before your message.', o)
      }
      case 'stderr': return this.notice('warn', 'Claude Code', String(o.text ?? ''), o)
      case 'bad_line': return this.add({ kind: 'unknown', id: this.id('u'), label: 'not JSON', raw: o }, null)
      default: return this.add({ kind: 'unknown', id: this.id('u'), label: labelOf(o), raw: o }, null)
    }
  }

  /** Permission requests still waiting for a person. */
  pending(): Array<Extract<Block, { kind: 'permission' }>> {
    return [...this.perms.values()].filter((p) => !p.answer)
  }
}

/** A whole session file (one JSON object per line) as a timeline; a line that is not JSON is kept as such. */
export function timelineOf(ndjson: string): Timeline {
  const t = new Timeline()
  for (const line of ndjson.split('\n')) {
    if (!line.trim()) continue
    try { t.push(JSON.parse(line)) } catch { t.push({ type: 'trisof', subtype: 'bad_line', line: line.slice(0, 2000) }) }
  }
  return t
}
